import React, { useState, useEffect } from "react"

import { Idea, Project } from "../../../../../model/types"
import { useNana } from "../../../../hooks/useNana"
import { useNavigate } from "react-router-dom"
import { ProjectsPageState } from "../../projects_page"
import FuzzyProjectFinder from "../../../../components/fuzzy_project_finder"
import * as Commands from "../../../../../model/commands"
import classNames from "classnames"

const metaLabelClass =
  "w-16 shrink-0 text-[11px] font-semibold uppercase tracking-[0.12em] text-stone-400 dark:text-stone-500"

const WithinProjects = ({ idea }: { idea: Idea }) => {
  const { model, commander, hub } = useNana()
  const navigate = useNavigate()

  const [withinProjects, setWithinProjects] = useState<Project[]>([])
  useEffect(() => {
    setWithinProjects(model.getProjectsForIdea(idea.id))
  }, [idea.id])

  const addIdeaToProject = (project: Project) => {
    commander.exec(
      new Commands.SetProjectFieldsCommand({
        id: project.id,
        fields: {
          ideaIds: project.ideaIds.concat([idea.id]),
        },
      })
    )
  }

  const [fuzzyFinderOpen, setFuzzyFinderOpen] = useState(false)

  useEffect(() => {
    const keys = [
      hub.on("model:project-updated", () =>
        setWithinProjects(model.getProjectsForIdea(idea.id))
      ),
      hub.on("model:project-deleted", () =>
        setWithinProjects(model.getProjectsForIdea(idea.id))
      ),
    ]

    return () => {
      keys.forEach((k) => hub.remove(k))
    }
  }, [idea.id])

  return (
    <div
      className={classNames(
        "px-5 py-2 md:px-7",
        // Separate from source row only when both are present (ideas only show source)
        idea.type === "idea" && "border-t border-stone-200/50 dark:border-white/[0.06]"
      )}
    >
      {fuzzyFinderOpen && (
        <FuzzyProjectFinder
          title="Choose project to add this idea to"
          includeCompleted={false}
          chooseProject={(project) => addIdeaToProject(project)}
          close={() => setFuzzyFinderOpen(false)}
        />
      )}
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 flex-1 items-start gap-3">
          <div
            className={classNames(metaLabelClass, "pt-1", {
              "text-stone-300 dark:text-stone-600": withinProjects.length === 0,
            })}
          >
            Projects
          </div>
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5 pt-0.5">
            {withinProjects.map((project) => (
              <button
                type="button"
                key={project.id}
                className="inline-flex max-w-full items-center truncate rounded-full bg-stone-900/[0.05] px-2.5 py-1 text-[12px] font-medium text-stone-700 transition-colors hover:bg-stone-900/[0.09] dark:bg-white/[0.08] dark:text-stone-200 dark:hover:bg-white/[0.12]"
                onClick={() => {
                  ProjectsPageState.openProjectId = project.id
                  ProjectsPageState.lastSelectedTab = project.status
                  navigate("/app/projects")
                }}
              >
                {project.name}
              </button>
            ))}
            {withinProjects.length === 0 && (
              <span className="py-1 text-[13px] text-stone-400 dark:text-stone-500">
                —
              </span>
            )}
          </div>
        </div>
        <button
          type="button"
          id="set-source-to-self"
          onClick={() => setFuzzyFinderOpen(true)}
          className="inline-flex h-7 shrink-0 cursor-pointer items-center rounded-full px-2.5 text-[12px] font-medium text-stone-500 transition-colors hover:bg-stone-900/[0.06] hover:text-stone-800 dark:text-stone-400 dark:hover:bg-white/[0.08] dark:hover:text-stone-100"
        >
          Add
        </button>
      </div>
    </div>
  )
}

export default WithinProjects
